import { useState, useMemo } from 'react';
import type { POI } from '@poi-planner/shared';
import { FOCUS_RADIUS_KM } from '@poi-planner/shared';
import { sortByProximity, haversineDistanceKm } from '../services/geoService';

export type SortMode = 'alpha' | 'timeBudget' | 'recentlyAdded' | 'distance';
export type TagFilterMode = 'include' | 'exclude';

/** Map viewport bounds */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface UsePoiFiltersOptions {
  /** Initial sort mode */
  initialSortMode?: SortMode;
  /** Initial sort direction */
  initialSortAscending?: boolean;
  /** Filter by map viewport bounds (Library page) */
  boundsFilter?: {
    enabled: boolean;
    bounds: MapBounds | null;
  };
  /** Hide scheduled POIs - pass scheduled counts map (trip context) */
  hideScheduled?: {
    enabled: boolean;
    scheduledCounts: Map<string, number>;
  };
  /** Focus mode - only show POIs near scheduled items (trip context) */
  focusMode?: {
    enabled: boolean;
    scheduledCoords: { lat: number; lng: number }[];
  };
  /** Distance sorting target */
  distanceTarget?: {
    targetPoiId: string | null;
    allPois: POI[];
  };
  /** All unique source texts for autocomplete */
  allSourceTexts?: string[];
  /** Collection POI mapping for collection filter */
  collectionPoiMap?: Map<string, Set<string>>;
  /** POI IDs used in each trip (for trip filter) - map of tripId -> Set of poiIds */
  tripPoiIdsMap?: Map<string, Set<string>>;
  /** Simple trip filter - only show POIs in the given set (sidebar "trip filter" toggle) */
  tripFilter?: {
    enabled: boolean;
    poiIds: Set<string>;
  };
}

export interface TagWithCount {
  tag: string;
  count: number;
}

export interface ActiveFilterCounts {
  tag: number;
  trip: number;
  collection: number;
  source: number;
  total: number;
}

export interface UsePoiFiltersResult {
  // State
  search: string;
  sortMode: SortMode;
  sortAscending: boolean;
  tagFilters: Map<string, TagFilterMode>;
  sourceFilters: Map<string, TagFilterMode>;
  collectionFilters: Map<string, TagFilterMode>;
  tripFilters: Map<string, TagFilterMode>;

  // Setters
  setSearch: (value: string) => void;
  setSortMode: (mode: SortMode) => void;
  setSortAscending: (ascending: boolean) => void;
  toggleTag: (tag: string) => void;
  clearTags: () => void;
  toggleSource: (source: string) => void;
  clearSources: () => void;
  toggleCollection: (collectionId: string) => void;
  clearCollections: () => void;
  toggleTrip: (tripId: string) => void;
  clearTrips: () => void;
  clearAllFilters: () => void;

  // Derived
  /** All tags with counts (computed from filtered POIs, reflects current selection) */
  allTags: TagWithCount[];
  filteredPOIs: POI[];
  /** Distance from target for each POI (only populated when distance target is set) */
  distanceMap: Map<string, number>;
  /** All unique source texts from all POIs */
  allSourceTexts: string[];
  /** Count of active filters per category */
  activeFilterCounts: ActiveFilterCounts;
}

/** Check if a POI is within map bounds */
function isWithinBounds(poi: POI, bounds: MapBounds): boolean {
  if (poi.lat == null || poi.lng == null) return false;
  return (
    poi.lat >= bounds.south &&
    poi.lat <= bounds.north &&
    poi.lng >= bounds.west &&
    poi.lng <= bounds.east
  );
}

/** Generic toggle for Map<string, TagFilterMode> */
function toggleFilterMode(
  prev: Map<string, TagFilterMode>,
  key: string
): Map<string, TagFilterMode> {
  const next = new Map(prev);
  const current = next.get(key);
  if (!current) {
    next.set(key, 'include');  // off → include
  } else if (current === 'include') {
    next.set(key, 'exclude');  // include → exclude
  } else {
    next.delete(key);          // exclude → off
  }
  return next;
}

/**
 * Reusable hook for filtering and sorting POIs.
 * Used in Sidebar (trip context) and LibraryPage (standalone).
 */
export function usePoiFilters(
  pois: POI[],
  options: UsePoiFiltersOptions = {}
): UsePoiFiltersResult {
  const {
    initialSortMode = 'alpha',
    initialSortAscending = true,
    boundsFilter,
    hideScheduled,
    focusMode,
    distanceTarget,
    collectionPoiMap,
    tripPoiIdsMap,
    tripFilter,
  } = options;

  const [search, setSearch] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>(initialSortMode);
  const [sortAscending, setSortAscending] = useState(initialSortAscending);
  const [tagFilters, setTagFilters] = useState<Map<string, TagFilterMode>>(new Map());
  const [sourceFilters, setSourceFilters] = useState<Map<string, TagFilterMode>>(new Map());
  const [collectionFilters, setCollectionFilters] = useState<Map<string, TagFilterMode>>(new Map());
  const [tripFilters, setTripFilters] = useState<Map<string, TagFilterMode>>(new Map());

  // Calculate distances from target POI
  const distanceMap = useMemo(() => {
    const map = new Map<string, number>();
    if (!distanceTarget?.targetPoiId) return map;

    const target = distanceTarget.allPois.find((p) => p.id === distanceTarget.targetPoiId);
    if (!target?.lat || !target?.lng) return map;

    const origin = { lat: target.lat, lng: target.lng };
    for (const poi of distanceTarget.allPois) {
      if (poi.lat != null && poi.lng != null) {
        map.set(poi.id, haversineDistanceKm(origin, { lat: poi.lat, lng: poi.lng }));
      }
    }
    return map;
  }, [distanceTarget?.targetPoiId, distanceTarget?.allPois]);

  // Base filtered POIs - apply structural filters first (before tag filter)
  const baseFilteredPOIs = useMemo(() => {
    let result = pois;

    // Hide scheduled filter (trip context)
    if (hideScheduled?.enabled && hideScheduled.scheduledCounts) {
      result = result.filter((p) => !hideScheduled.scheduledCounts.has(p.id));
    }

    // Focus mode filter - only show POIs within FOCUS_RADIUS_KM of scheduled items (trip context)
    if (focusMode?.enabled && focusMode.scheduledCoords.length > 0) {
      result = result.filter((poi) => {
        if (poi.lat == null || poi.lng == null) return false;
        const poiCoord = { lat: poi.lat, lng: poi.lng };
        return focusMode.scheduledCoords.some(
          (origin) => haversineDistanceKm(origin, poiCoord) <= FOCUS_RADIUS_KM
        );
      });
    }

    // Bounds filter (Library page - hide POIs outside map viewport)
    if (boundsFilter?.enabled && boundsFilter.bounds) {
      result = result.filter((p) => isWithinBounds(p, boundsFilter.bounds!));
    }

    // Text filter
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(q));
    }

    // Source filters (multi-select with include/exclude)
    if (sourceFilters.size > 0) {
      const includeSources = [...sourceFilters.entries()]
        .filter(([_, mode]) => mode === 'include')
        .map(([source]) => source.toLowerCase());
      const excludeSources = [...sourceFilters.entries()]
        .filter(([_, mode]) => mode === 'exclude')
        .map(([source]) => source.toLowerCase());

      result = result.filter((p) => {
        const poiSources = (p.sources ?? []).map((s) => s.text.toLowerCase());
        // Must have ANY of the included sources (OR)
        const matchesInclude = includeSources.length === 0 ||
          includeSources.some((src) => poiSources.some((ps) => ps.includes(src)));
        // Must NOT have ANY of the excluded sources (AND NOT)
        const matchesExclude = !excludeSources.some((src) =>
          poiSources.some((ps) => ps.includes(src))
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Collection filters (multi-select with include/exclude)
    if (collectionFilters.size > 0 && collectionPoiMap) {
      const includeCollections = [...collectionFilters.entries()]
        .filter(([_, mode]) => mode === 'include')
        .map(([id]) => id);
      const excludeCollections = [...collectionFilters.entries()]
        .filter(([_, mode]) => mode === 'exclude')
        .map(([id]) => id);

      result = result.filter((p) => {
        // Must be in ANY of the included collections (OR)
        const matchesInclude = includeCollections.length === 0 ||
          includeCollections.some((colId) => collectionPoiMap.get(colId)?.has(p.id));
        // Must NOT be in ANY of the excluded collections (AND NOT)
        const matchesExclude = !excludeCollections.some((colId) =>
          collectionPoiMap.get(colId)?.has(p.id)
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Trip filters (multi-select with include/exclude)
    if (tripFilters.size > 0 && tripPoiIdsMap) {
      const includeTrips = [...tripFilters.entries()]
        .filter(([_, mode]) => mode === 'include')
        .map(([id]) => id);
      const excludeTrips = [...tripFilters.entries()]
        .filter(([_, mode]) => mode === 'exclude')
        .map(([id]) => id);

      result = result.filter((p) => {
        // Must be used in ANY of the included trips (OR)
        const matchesInclude = includeTrips.length === 0 ||
          includeTrips.some((tripId) => tripPoiIdsMap.get(tripId)?.has(p.id));
        // Must NOT be used in ANY of the excluded trips (AND NOT)
        const matchesExclude = !excludeTrips.some((tripId) =>
          tripPoiIdsMap.get(tripId)?.has(p.id)
        );
        return matchesInclude && matchesExclude;
      });
    }

    // Simple trip filter - only show POIs in the current trip (pinned + scheduled)
    if (tripFilter?.enabled && tripFilter.poiIds.size > 0) {
      result = result.filter((p) => tripFilter.poiIds.has(p.id));
    }

    return result;
  }, [pois, hideScheduled, focusMode, boundsFilter, search, sourceFilters, collectionFilters, collectionPoiMap, tripFilters, tripPoiIdsMap, tripFilter]);

  // Filter and sort POIs (starts from baseFilteredPOIs, applies tag filter and sorting)
  const filteredPOIs = useMemo(() => {
    let result = baseFilteredPOIs;

    // Tag filter - AND logic for includes, exclude any with excluded tags
    if (tagFilters.size > 0) {
      const includeTags = [...tagFilters.entries()]
        .filter(([_, mode]) => mode === 'include')
        .map(([tag]) => tag);
      const excludeTags = [...tagFilters.entries()]
        .filter(([_, mode]) => mode === 'exclude')
        .map(([tag]) => tag);

      result = result.filter((p) => {
        const poiTags = p.tags ?? [];
        // Must have ALL included tags
        const hasAllIncludes = includeTags.every((t) => poiTags.includes(t));
        // Must NOT have ANY excluded tags
        const hasNoExcludes = !excludeTags.some((t) => poiTags.includes(t));
        return hasAllIncludes && hasNoExcludes;
      });
    }

    // Sort
    switch (sortMode) {
      case 'alpha':
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'timeBudget':
        result = [...result].sort((a, b) => b.timeBudget - a.timeBudget);
        break;
      case 'recentlyAdded':
        result = [...result].reverse();
        break;
      case 'distance': {
        const target = distanceTarget?.allPois.find((p) => p.id === distanceTarget?.targetPoiId);
        if (target?.lat != null && target?.lng != null) {
          const origin = { lat: target.lat, lng: target.lng };
          const sorted = sortByProximity(result, origin);
          result = sorted.map((wd) => wd.item);
        }
        break;
      }
    }

    // Sort direction (distance mode is always ascending - nearest first)
    if (!sortAscending && sortMode !== 'distance') {
      result = result.reverse();
    }

    return result;
  }, [baseFilteredPOIs, sortMode, sortAscending, tagFilters, distanceTarget]);

  // Derive all unique tags with counts from filtered POIs (progressive refinement)
  // Also include any actively filtered tags so users can toggle them off
  const allTags = useMemo(() => {
    const tagCounts = new Map<string, number>();
    for (const poi of filteredPOIs) {
      for (const tag of poi.tags ?? []) {
        tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
      }
    }
    // Ensure actively filtered tags are always visible (even with 0 count)
    for (const tag of tagFilters.keys()) {
      if (!tagCounts.has(tag)) {
        tagCounts.set(tag, 0);
      }
    }
    // Sort: included first, then excluded, then unselected
    // Within each group, sort by count descending, then alphabetically
    return Array.from(tagCounts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => {
        const aMode = tagFilters.get(a.tag);
        const bMode = tagFilters.get(b.tag);
        const aPriority = aMode === 'include' ? 0 : aMode === 'exclude' ? 1 : 2;
        const bPriority = bMode === 'include' ? 0 : bMode === 'exclude' ? 1 : 2;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return b.count - a.count || a.tag.localeCompare(b.tag);
      });
  }, [filteredPOIs, tagFilters]);

  // Derive all unique source texts from all POIs (for autocomplete)
  const allSourceTexts = useMemo(() => {
    const sourceSet = new Set<string>();
    for (const poi of pois) {
      for (const source of poi.sources ?? []) {
        if (source.text.trim()) {
          sourceSet.add(source.text.trim());
        }
      }
    }
    return Array.from(sourceSet).sort();
  }, [pois]);

  const toggleTag = (tag: string) => {
    setTagFilters((prev) => toggleFilterMode(prev, tag));
  };

  const clearTags = () => {
    setTagFilters(new Map());
  };

  const toggleSource = (source: string) => {
    setSourceFilters((prev) => toggleFilterMode(prev, source));
  };

  const clearSources = () => {
    setSourceFilters(new Map());
  };

  const toggleCollection = (collectionId: string) => {
    setCollectionFilters((prev) => toggleFilterMode(prev, collectionId));
  };

  const clearCollections = () => {
    setCollectionFilters(new Map());
  };

  const toggleTrip = (tripId: string) => {
    setTripFilters((prev) => toggleFilterMode(prev, tripId));
  };

  const clearTrips = () => {
    setTripFilters(new Map());
  };

  const clearAllFilters = () => {
    setTagFilters(new Map());
    setSourceFilters(new Map());
    setCollectionFilters(new Map());
    setTripFilters(new Map());
  };

  // Count active filters per category
  const activeFilterCounts = useMemo<ActiveFilterCounts>(() => ({
    tag: tagFilters.size,
    trip: tripFilters.size,
    collection: collectionFilters.size,
    source: sourceFilters.size,
    total: tagFilters.size + tripFilters.size + collectionFilters.size + sourceFilters.size,
  }), [tagFilters, tripFilters, collectionFilters, sourceFilters]);

  return {
    // State
    search,
    sortMode,
    sortAscending,
    tagFilters,
    sourceFilters,
    collectionFilters,
    tripFilters,

    // Setters
    setSearch,
    setSortMode,
    setSortAscending,
    toggleTag,
    clearTags,
    toggleSource,
    clearSources,
    toggleCollection,
    clearCollections,
    toggleTrip,
    clearTrips,
    clearAllFilters,

    // Derived
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    activeFilterCounts,
  };
}
